import {Box, useTheme} from '@mui/material';
import {HomeTitle, Title} from '../components/common/text';
import {PageSeo, PageType} from '../components/common/container';
import {STORAGE, URL_SERVER_INTERNAL} from '../utils/settings';
import {getCampaignsHome, getPastCampaigns} from '../utils/campaign';

import CampaignHistory from '../components/campaign/campaignHistory';
import CampaignHome from '../components/campaign/campaignHome';
import {CampaignOverview} from '../types';
import {Colors} from '../components/common/colors';
import {GetStaticProps} from 'next';
import {serverSideTranslations} from 'next-i18next/serverSideTranslations';
import {sorted} from '../utils/list';
import {useMemo} from 'react';
import {useTranslation} from 'next-i18next';

export default function HistoryScreen(props: HistoryStaticProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';

    const {t} = useTranslation('common');

    const _pastCampaigns = getPastCampaigns(props.data);
    const _activeCampaigns = getCampaignsHome(props.data);
    const pastCampaigns = useMemo(() => sortedCampaigns(_pastCampaigns), [_pastCampaigns])
        // TODO: filter our production test campaign, always:
        .filter((c) => c.campaign_id !== '97ee211f-1867-4a19-8ef5-295436ba1bda');
    const activeCampaigns = useMemo(
        () => sortedCampaigns(_activeCampaigns),
        [_activeCampaigns]
    )
        // TODO: filter our production test campaign, always:
        .filter((c) => c.campaign_id !== '97ee211f-1867-4a19-8ef5-295436ba1bda');

    const bgStar = STORAGE + '/bg/' + (isDark ? 'title.svg?v=3' : 'title.svg?v=3');
    const bgCoin = STORAGE + '/pattern/' + 'piece.webp';

    const gradient = isDark
        ? 'linear-gradient(#1c1c1e, #141414)'
        : 'linear-gradient(white, #f0f0f0)';

    return (
        <>
            <PageSeo page={PageType.HISTORY} />

            <Box
                sx={{
                    backgroundImage: 'url(' + bgStar + '), ' + gradient,
                    paddingTop: '50px',
                    paddingBottom: '60px',
                }}
            >
                <Title>{t('title.history')}</Title>
            </Box>
            <Box>
                {pastCampaigns.map((c) => (
                    <CampaignHistory campaign={c} key={c.campaign_id} />
                ))}
            </Box>
            {activeCampaigns.length > 0 && (
                <>
                    <Box
                        sx={{
                            clipPath: 'polygon(0 0, 100% 5vw, 100% 100%, 0 calc(100% - 5vw))',
                        }}
                    >
                        <Box
                            sx={{
                                backgroundImage:
                                    'url(' +
                                    bgCoin +
                                    '), linear-gradient(to right bottom, #ffd14f, #fce39d)',
                                paddingTop: '150px',
                                paddingBottom: '150px',
                                paddingRight: '5px',
                                paddingLeft: '5px',
                            }}
                        >
                            <HomeTitle color={Colors.grey}>
                                {t('title.pending_campaign')}
                            </HomeTitle>
                        </Box>
                    </Box>
                    <Box>
                        {activeCampaigns.map((c) => (
                            <CampaignHome campaign={c} key={c.campaign_id} />
                        ))}
                    </Box>
                </>
            )}
        </>
    );
}

export type HistoryStaticProps = {
    data: CampaignOverview[];
};

export const getStaticProps: GetStaticProps<HistoryStaticProps> = async (context) => {
    const translation = await serverSideTranslations(context.locale, ['common']);
    try {
        let url = URL_SERVER_INTERNAL + '/campaign';
        let requestOptions = {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        };

        const result = await fetch(url, requestOptions);
        const json = await result.json();

        return {
            props: {
                data: json,
                ...translation,
            },
            revalidate: 5,
        };
    } catch (e) {
        console.log('getServerSideProps::error /compaign ', e);
        return {
            props: {
                data: null,
                ...translation,
            },
            revalidate: 5,
        };
    }
};

function sortedCampaigns(campaigns: CampaignOverview[]) {
    return sorted(
        campaigns,
        (c) => new Date(c.last_end_datetime ?? c.end_datetime).getTime(),
        true
    );
}
